import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { LineChart, Line, XAxis, YAxis, Tooltip, ReferenceLine, ResponsiveContainer, CartesianGrid, ReferenceDot } from 'recharts';

var API = 'https://api.algrok.com';

function fmtRs(n){if(n==null||isNaN(n))return'--';return'\u20B9'+Number(n).toFixed(1);}

function StraddleTracker(){
  var [state,setState]=useState({});
  var [history,setHistory]=useState([]);
  var [events,setEvents]=useState([]);
  var [pnlPairs,setPnlPairs]=useState([]);
  var pollRef=useRef(null);

  var fetchAll=useCallback(function(){
    fetch(API+'/straddle/state').then(function(r){return r.json();}).then(function(d){
      if(d && !d.error) setState(d);
    }).catch(function(){});
    fetch(API+'/straddle/history').then(function(r){return r.json();}).then(function(d){
      if(d && d.history) setHistory(d.history);
    }).catch(function(){});
    fetch(API+'/straddle/events').then(function(r){return r.json();}).then(function(d){
      if(d && d.events) setEvents(d.events);
    }).catch(function(){});
    fetch(API+'/straddle/pnl').then(function(r){return r.json();}).then(function(d){
      if(!d) return;
      if(d.pnl_pairs) setPnlPairs(d.pnl_pairs);
    }).catch(function(){});
  },[]);

  useEffect(function(){
    fetchAll();
    pollRef.current=setInterval(fetchAll,10000);
    return function(){if(pollRef.current)clearInterval(pollRef.current);};
  },[fetchAll]);

  var cfg=state.config||{};
  var setCfg=function(key,val){
    var payload={};payload[key]=val;
    fetch(API+'/straddle/config',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify(payload)})
      .then(function(){fetchAll();}).catch(function(){});
  };

  // Annotate history with roll markers + entry/exit markers for chart
  var chartData=useMemo(function(){
    return history.map(function(h,i){
      return {
        time:h.time,
        combined:h.combined,
        vwap:h.vwap,
        spot:h.spot,
        is_roll:h.is_roll,
        side:h.side,
        idx:i,
      };
    });
  },[history]);

  // Identify event indices on history for ReferenceDot overlays
  var eventDots=useMemo(function(){
    var dots=[];
    var histTimes=history.map(function(h){return h.time;});
    events.forEach(function(ev){
      var idx=histTimes.indexOf(ev.time);
      if(idx>=0){
        dots.push({
          x:ev.time,
          y:ev.combined||history[idx].combined,
          type:ev.type,
        });
      }
    });
    return dots;
  },[events,history]);

  var sideColor=state.side==='in'?'#22c55e':state.side==='out'?'#ef4444':'#9ca3af';
  var sideLabel=state.side==='in'?'IN (combined ≤ VWAP)':state.side==='out'?'OUT (combined > VWAP)':'—';
  var posStatus=state.position_status||'WAITING';
  var posColor=posStatus==='OPEN'?'#22c55e':'#9ca3af';
  var dayPnl=state.day_pnl||0;
  var pnlColor=dayPnl>0?'#22c55e':dayPnl<0?'#ef4444':'#9ca3af';
  var dayPnlTotal=state.day_pnl_total||0;
  var dayPnlTotalColor=dayPnlTotal>0?'#22c55e':dayPnlTotal<0?'#ef4444':'#9ca3af';
  var disabledToday=!!state.trade_disabled_today;

  // Live trading was removed from straddle_tracker.py on 16-Aug-2026 — the
  // module has no order path at all now. exit_all_manual still stops tracking
  // for the day, which is worth keeping, so the button was relabelled rather
  // than deleted.
  var doStopToday=function(){
    if(!window.confirm('Stop tracking for today?\\n\\nSamples and signals pause until tomorrow.')) return;
    fetch(API+'/straddle/exit-all',{method:'POST'})
      .then(function(r){return r.json();})
      .then(function(d){
        if(!(d&&d.ok)) alert('Failed: '+(d&&d.error?d.error:'unknown'));
        fetchAll();
      }).catch(function(e){alert('Error: '+e.message);});
  };

  var lbl={display:'flex',flexDirection:'column',gap:4,fontSize:11,color:'#9ca3af',fontWeight:600};
  var inp={background:'#0a0f1c',border:'1px solid #1f2937',color:'#e5e7eb',padding:'6px 8px',borderRadius:6,fontSize:12,fontWeight:600};

  return (
    <div style={{padding:16,minHeight:'calc(100vh - 80px)',color:'#e5e7eb'}}>
      {/* Header */}
      <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:12,flexWrap:'wrap',gap:8}}>
        <div style={{display:'flex',alignItems:'center',gap:12,flexWrap:'wrap'}}>
          <span style={{fontSize:18,fontWeight:800}}>📈 ATM Straddle Tracker</span>
          {disabledToday&&<span style={{fontSize:9,padding:'3px 10px',background:'rgba(156,163,175,0.18)',color:'#9ca3af',borderRadius:4,fontWeight:800,border:'1px solid rgba(156,163,175,0.3)'}}>TRACKING STOPPED TODAY</span>}
          {!state.broker_connected&&<span style={{fontSize:9,padding:'3px 10px',background:'rgba(251,191,36,0.15)',color:'#fbbf24',borderRadius:4,fontWeight:700,border:'1px solid rgba(251,191,36,0.3)'}}>BROKER OFFLINE</span>}
        </div>
        <div style={{display:'flex',alignItems:'center',gap:8}}>
          {!disabledToday&&<button onClick={doStopToday} style={{background:'rgba(156,163,175,0.15)',color:'#9ca3af',border:'1px solid rgba(156,163,175,0.3)',padding:'7px 14px',borderRadius:6,fontSize:11,fontWeight:800,cursor:'pointer'}}>Stop tracking today</button>}
          <div style={{fontSize:11,color:'#9ca3af'}}>🕐 {state.last_compute_time||'--'}</div>
        </div>
      </div>

      {/* Headline strip */}
      <div style={{display:'grid',gridTemplateColumns:'repeat(8,1fr)',gap:8,marginBottom:12}}>
        <div style={{background:'#0a0f1c',border:'1px solid #1f2937',borderRadius:8,padding:'8px 10px'}}>
          <div style={{fontSize:9,color:'#6b7280'}}>ATM Strike</div>
          <div style={{fontSize:14,fontWeight:700}}>{state.atm_strike||'--'}</div>
        </div>
        <div style={{background:'#0a0f1c',border:'1px solid #1f2937',borderRadius:8,padding:'8px 10px'}}>
          <div style={{fontSize:9,color:'#6b7280'}}>NIFTY Spot</div>
          <div style={{fontSize:14,fontWeight:700}}>{state.spot?Number(state.spot).toFixed(1):'--'}</div>
        </div>
        <div style={{background:'#0a0f1c',border:'1px solid #1f2937',borderRadius:8,padding:'8px 10px'}}>
          <div style={{fontSize:9,color:'#6b7280'}}>Combined Premium</div>
          <div style={{fontSize:14,fontWeight:700,color:'#22d3ee'}}>{fmtRs(state.combined)}</div>
          <div style={{fontSize:9,color:'#6b7280'}}>CE {fmtRs(state.ce_ltp)} + PE {fmtRs(state.pe_ltp)}</div>
        </div>
        <div style={{background:'#0a0f1c',border:'1px solid #1f2937',borderRadius:8,padding:'8px 10px'}}>
          <div style={{fontSize:9,color:'#6b7280'}}>VWAP</div>
          <div style={{fontSize:14,fontWeight:700,color:'#a78bfa'}}>{fmtRs(state.vwap)}</div>
        </div>
        <div style={{background:'#0a0f1c',border:'1px solid #1f2937',borderRadius:8,padding:'8px 10px'}}>
          <div style={{fontSize:9,color:'#6b7280'}}>Side</div>
          <div style={{fontSize:13,fontWeight:700,color:sideColor}}>{sideLabel}</div>
        </div>
        <div style={{background:'#0a0f1c',border:'1px solid #1f2937',borderRadius:8,padding:'8px 10px'}}>
          <div style={{fontSize:9,color:'#6b7280'}}>Position</div>
          <div style={{fontSize:13,fontWeight:700,color:posColor}}>{posStatus==='OPEN'?'🟢 OPEN':'⚪ WAITING'}</div>
          {state.position_entry&&<div style={{fontSize:9,color:'#6b7280'}}>{state.position_entry.strike} @ ₹{state.position_entry.combined}</div>}
        </div>
        <div style={{background:'#0a0f1c',border:'1px solid #1f2937',borderRadius:8,padding:'8px 10px'}}>
          <div style={{fontSize:9,color:'#6b7280'}}>Day P/L</div>
          <div style={{fontSize:14,fontWeight:700,color:pnlColor}}>₹{dayPnl>=0?'+':''}{dayPnl.toFixed(1)}</div>
          <div style={{fontSize:9,color:'#6b7280'}}>per lot · {state.pnl_pairs_count||0} pairs</div>
        </div>
        <div style={{background:'#0a0f1c',border:'1px solid #1f2937',borderRadius:8,padding:'8px 10px'}}>
          <div style={{fontSize:9,color:'#6b7280'}}>Today</div>
          <div style={{fontSize:11,fontWeight:700}}>{state.rolls_today||0} rolls · {state.entries_today||0} ↓ · {state.exits_today||0} ↑</div>
        </div>
      </div>


      <div style={{display:'grid',gridTemplateColumns:'240px 1fr 280px',gap:12}}>
        {/* Settings panel */}
        <div style={{background:'#0a0f1c',border:'1px solid #1f2937',borderRadius:10,padding:14,height:'fit-content'}}>
          <div style={{fontSize:13,fontWeight:700,marginBottom:14}}>Settings</div>

          <div style={lbl}>VWAP Type
            <select value={cfg.vwap_type||'combined_vwap'} onChange={function(e){setCfg('vwap_type',e.target.value);}} style={inp}>
              <option value="combined_vwap">Combined Premium VWAP (default)</option>
              <option value="spot_vwap">NIFTY Spot VWAP</option>
              <option value="simple_ma">Simple Moving Average</option>
              <option value="anchored_session_start">Anchored — Session Start</option>
              <option value="anchored_last_signal">Anchored — Last Signal</option>
              <option value="anchored_extreme">Anchored — Session Extreme</option>
              <option value="anchored_user">Anchored — User Click</option>
            </select>
          </div>

          <div style={{...lbl,marginTop:12}}>Roll Trigger
            <select value={cfg.roll_trigger||'past_50pts'} onChange={function(e){setCfg('roll_trigger',e.target.value);}} style={inp}>
              <option value="past_50pts">Past 50pts from ATM (default)</option>
              <option value="immediate">Immediate (any ATM change)</option>
              <option value="pure_anchored_vwap_sl">Pure Anchored VWAP SL (no spot trigger)</option>
            </select>
            {cfg.roll_trigger==='pure_anchored_vwap_sl'&&<div style={{fontSize:9,color:'#6b7280',marginTop:2}}>
              Exit only when combined &gt; VWAP. Position holds at entry strike regardless of spot drift.
            </div>}
          </div>

          {cfg.vwap_type==='simple_ma'&&<div style={{...lbl,marginTop:12}}>MA Window (samples × 10s)
            <input type="number" min={5} max={360} value={cfg.ma_window||30} onChange={function(e){setCfg('ma_window',+e.target.value);}} style={inp}/>
          </div>}

          {cfg.vwap_type==='anchored_extreme'&&<div style={{...lbl,marginTop:12}}>Extreme Dead-band (%)
            <input type="number" min={0.5} max={50} step={0.5} value={cfg.extreme_deadband_pct||5.0} onChange={function(e){setCfg('extreme_deadband_pct',+e.target.value);}} style={inp}/>
            <div style={{fontSize:9,color:'#6b7280',marginTop:2}}>Re-anchor only if new extreme is &gt;{cfg.extreme_deadband_pct||5.0}% from anchor</div>
          </div>}

          {cfg.vwap_type==='anchored_user'&&<div style={{...lbl,marginTop:12}}>
            <button onClick={function(){fetch(API+'/straddle/anchor-now',{method:'POST'}).then(function(){fetchAll();}).catch(function(){});}} style={{background:'#1e3a8a',color:'#dbeafe',border:'1px solid #3b82f6',padding:'8px 12px',borderRadius:6,fontSize:12,fontWeight:700,cursor:'pointer'}}>📍 Anchor Now</button>
            <div style={{fontSize:9,color:'#6b7280',marginTop:2}}>Resets VWAP to current combined</div>
          </div>}

          {state.anchor_time&&cfg.vwap_type&&cfg.vwap_type.indexOf('anchored_')===0&&<div style={{marginTop:12,padding:'8px 10px',background:'rgba(59,130,246,0.08)',border:'1px solid rgba(59,130,246,0.25)',borderRadius:6,fontSize:10}}>
            <div style={{color:'#93c5fd',fontWeight:700,marginBottom:2}}>📍 Last anchor</div>
            <div style={{color:'#9ca3af'}}>{state.anchor_time} @ ₹{state.anchor_combined}</div>
          </div>}

          <div style={{marginTop:18,paddingTop:12,borderTop:'1px solid #1f2937',fontSize:9,color:'#6b7280',lineHeight:1.5}}>
            <div style={{marginBottom:6}}><b>How it works:</b></div>
            <div>• Tracks ATM straddle (CE+PE) combined premium every 10s</div>
            <div>• Rolls to new ATM when spot moves past trigger</div>
            <div>• VWAP resets on day-start and every roll</div>
            <div>• Crossover signals confirmed by 2 consecutive samples</div>
            <div>• ENTRY signal: combined falls below VWAP</div>
            <div>• EXIT signal: combined rises above VWAP</div>
            <div>• Signals only — this module places no orders</div>
          </div>
        </div>

        {/* Chart */}
        <div style={{background:'#0a0f1c',border:'1px solid #1f2937',borderRadius:10,padding:14}}>
          <div style={{height:480}}>
            {chartData.length===0?
              <div style={{display:'flex',height:'100%',alignItems:'center',justifyContent:'center',color:'#6b7280',fontSize:13}}>
                Waiting for live data — market closed or just opened
              </div>
            :
              <ResponsiveContainer>
                <LineChart data={chartData} margin={{top:10,right:30,left:10,bottom:30}}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#1f2937"/>
                  <XAxis dataKey="time" stroke="#9ca3af" fontSize={10} interval="preserveStartEnd" minTickGap={50}/>
                  <YAxis stroke="#9ca3af" fontSize={10} tickFormatter={function(v){return '\u20B9'+v.toFixed(0);}} domain={['auto','auto']}/>
                  <Tooltip
                    contentStyle={{background:'#0a0f1c',border:'1px solid #1f2937',borderRadius:6,fontSize:11}}
                    formatter={function(v,name){return [fmtRs(v),name];}}
                    labelFormatter={function(t){return 'Time: '+t;}}
                  />
                  <Line type="monotone" dataKey="combined" stroke="#22d3ee" strokeWidth={2} dot={false} isAnimationActive={false} name="Combined"/>
                  <Line type="monotone" dataKey="vwap" stroke="#a78bfa" strokeWidth={1.5} strokeDasharray="5 3" dot={false} isAnimationActive={false} name="VWAP"/>
                  {/* Vertical lines at rolls */}
                  {chartData.filter(function(d){return d.is_roll;}).map(function(d,i){
                    return <ReferenceLine key={'r'+i} x={d.time} stroke="#fb923c" strokeDasharray="2 2" label={{value:'roll',fill:'#fb923c',fontSize:8,position:'top'}}/>;
                  })}
                  {/* Vertical lines at anchor resets */}
                  {events.filter(function(ev){return ev.type==='anchor';}).map(function(ev,i){
                    return <ReferenceLine key={'a'+i} x={ev.time} stroke="#3b82f6" strokeDasharray="3 4" label={{value:'anchor',fill:'#3b82f6',fontSize:8,position:'top'}}/>;
                  })}
                  {/* Event dots (entry/exit/anchor) */}
                  {eventDots.map(function(d,i){
                    var color=d.type==='entry'?'#22c55e':d.type==='exit'?'#ef4444':d.type==='anchor'?'#3b82f6':'#9ca3af';
                    return <ReferenceDot key={'e'+i} x={d.x} y={d.y} r={4} fill={color} stroke="#fff" strokeWidth={1}/>;
                  })}
                </LineChart>
              </ResponsiveContainer>
            }
          </div>
        </div>

        {/* Event log panel */}
        <div style={{background:'#0a0f1c',border:'1px solid #1f2937',borderRadius:10,padding:14,maxHeight:600,overflowY:'auto'}}>
          <div style={{fontSize:13,fontWeight:700,marginBottom:10,position:'sticky',top:0,background:'#0a0f1c',paddingBottom:6,borderBottom:'1px solid #1f2937'}}>📋 Event Log</div>
          {events.length===0?
            <div style={{fontSize:11,color:'#6b7280'}}>No events yet today.</div>
          :
            <div style={{display:'flex',flexDirection:'column',gap:8}}>
              {events.slice().reverse().map(function(ev,i){
                var color=ev.type==='roll'?'#fb923c':ev.type==='entry'?'#22c55e':ev.type==='exit'?'#ef4444':ev.type==='anchor'?'#3b82f6':'#9ca3af';
                var icon=ev.type==='roll'?'🔄':ev.type==='entry'?'⬇️':ev.type==='exit'?'⬆️':ev.type==='anchor'?'📍':'•';
                return <div key={i} style={{borderLeft:'3px solid '+color,paddingLeft:8,fontSize:10}}>
                  <div style={{display:'flex',gap:6,alignItems:'center'}}>
                    <span style={{color:color,fontWeight:700}}>{ev.time}</span>
                    <span style={{color:color,fontWeight:700,textTransform:'uppercase'}}>{icon} {ev.type}</span>
                  </div>
                  {ev.type==='roll'?
                    <div style={{color:'#9ca3af',marginTop:2}}>ATM → {ev.atm_strike} (spot {Number(ev.spot).toFixed(1)})<br/>{ev.reason}</div>
                  :ev.type==='anchor'?
                    <div style={{color:'#9ca3af',marginTop:2}}>New anchor @ ₹{ev.combined}<br/>{ev.reason}</div>
                  :
                    <div style={{color:'#9ca3af',marginTop:2}}>combined ₹{ev.combined} vs VWAP ₹{ev.vwap}<br/>ATM {ev.atm_strike} · spot {Number(ev.spot).toFixed(1)}</div>
                  }
                </div>;
              })}
            </div>
          }
        </div>
      </div>


      {/* P/L Panel — closed entry→exit pairs */}
      <div style={{background:'#0a0f1c',border:'1px solid #1f2937',borderRadius:10,padding:14,marginTop:12}}>
        <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:10,paddingBottom:6,borderBottom:'1px solid #1f2937'}}>
          <span style={{fontSize:13,fontWeight:700}}>💰 P/L per Trade</span>
          <span style={{fontSize:11,color:'#9ca3af'}}>
            <span>Per-lot: <span style={{color:pnlColor,fontWeight:700}}>₹{dayPnl>=0?'+':''}{dayPnl.toFixed(1)}</span></span>
            <span style={{marginLeft:12}}>Live ₹: <span style={{color:dayPnlTotalColor,fontWeight:700}}>₹{dayPnlTotal>=0?'+':''}{dayPnlTotal.toFixed(0)}</span></span>
            <span style={{marginLeft:8,color:'#6b7280'}}>({pnlPairs.length} closed)</span>
          </span>
        </div>
        {pnlPairs.length===0?
          <div style={{fontSize:11,color:'#6b7280',padding:'8px 0'}}>No closed trades yet. Entries require combined ≤ VWAP; exits fire on combined &gt; VWAP or roll (mode-dependent).</div>
        :
          <div style={{maxHeight:240,overflowY:'auto'}}>
            <table style={{width:'100%',fontSize:11,borderCollapse:'collapse'}}>
              <thead>
                <tr style={{color:'#6b7280',fontSize:10,textAlign:'left'}}>
                  <th style={{padding:'4px 8px',borderBottom:'1px solid #1f2937'}}>#</th>
                  <th style={{padding:'4px 8px',borderBottom:'1px solid #1f2937'}}>Mode</th>
                  <th style={{padding:'4px 8px',borderBottom:'1px solid #1f2937'}}>Entry</th>
                  <th style={{padding:'4px 8px',borderBottom:'1px solid #1f2937'}}>Exit</th>
                  <th style={{padding:'4px 8px',borderBottom:'1px solid #1f2937'}}>Strike</th>
                  <th style={{padding:'4px 8px',borderBottom:'1px solid #1f2937',textAlign:'right'}}>Entry ₹</th>
                  <th style={{padding:'4px 8px',borderBottom:'1px solid #1f2937',textAlign:'right'}}>Exit ₹</th>
                  <th style={{padding:'4px 8px',borderBottom:'1px solid #1f2937'}}>Reason</th>
                  <th style={{padding:'4px 8px',borderBottom:'1px solid #1f2937',textAlign:'right'}}>P/L/lot</th>
                  <th style={{padding:'4px 8px',borderBottom:'1px solid #1f2937',textAlign:'right'}}>P/L ₹</th>
                </tr>
              </thead>
              <tbody>
                {pnlPairs.slice().reverse().map(function(p,i){
                  var pnlC=p.pnl>0?'#22c55e':p.pnl<0?'#ef4444':'#9ca3af';
                  var rsnC=p.exit_reason==='vwap'?'#a78bfa':p.exit_reason==='roll'?'#fb923c':p.exit_reason==='manual'?'#ef4444':p.exit_reason==='end_time'?'#fbbf24':'#9ca3af';
                  var modeBadge=p.live?{txt:'LIVE',bg:'rgba(34,197,94,0.18)',fg:'#22c55e'}:{txt:'VIRT',bg:'rgba(156,163,175,0.15)',fg:'#9ca3af'};
                  return <tr key={i} style={{borderBottom:'1px solid rgba(31,41,55,0.5)'}}>
                    <td style={{padding:'4px 8px',color:'#6b7280'}}>{pnlPairs.length-i}</td>
                    <td style={{padding:'4px 8px'}}><span style={{padding:'1px 6px',borderRadius:3,fontSize:9,fontWeight:800,background:modeBadge.bg,color:modeBadge.fg}}>{modeBadge.txt}</span></td>
                    <td style={{padding:'4px 8px'}}>{p.entry_time}</td>
                    <td style={{padding:'4px 8px'}}>{p.exit_time}</td>
                    <td style={{padding:'4px 8px',color:'#9ca3af'}}>{p.strike}</td>
                    <td style={{padding:'4px 8px',textAlign:'right',color:'#22d3ee'}}>{p.entry_combined.toFixed(1)}</td>
                    <td style={{padding:'4px 8px',textAlign:'right',color:'#22d3ee'}}>{p.exit_combined.toFixed(1)}</td>
                    <td style={{padding:'4px 8px'}}><span style={{color:rsnC,fontSize:9,fontWeight:700,textTransform:'uppercase'}}>{p.exit_reason}</span></td>
                    <td style={{padding:'4px 8px',textAlign:'right',color:pnlC,fontWeight:700}}>{p.pnl>0?'+':''}{p.pnl.toFixed(1)}</td>
                    <td style={{padding:'4px 8px',textAlign:'right',color:pnlC,fontWeight:700}}>{p.pnl_total!=null?(p.pnl_total>=0?'+':'')+Math.round(p.pnl_total):'\u2014'}</td>
                  </tr>;
                })}
              </tbody>
            </table>
          </div>
        }
      </div>
    </div>
  );
}

export default StraddleTracker;
